export type TimeOfDayPhase = 'dawn' | 'day' | 'golden-hour' | 'dusk' | 'night';

export interface TimeOfDayState {
  phase: TimeOfDayPhase;
  label: string;
  description: string;
  accent: string;
  icon: 'sunrise' | 'sun' | 'sunset' | 'moon';
  minutes: number;
}

const MINUTES_PER_DAY = 24 * 60;

function normalizeMinutes(minutes: number): number {
  return ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

export function getMinutes(date: Date = new Date()): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function getTimeOfDayPhase(date: Date = new Date()): TimeOfDayPhase {
  const minutes = getMinutes(date);
  if (minutes < 6 * 60) return 'night';
  if (minutes < 7 * 60 + 30) return 'dawn';
  if (minutes < 17 * 60) return 'day';
  if (minutes < 19 * 60) return 'golden-hour';
  if (minutes < 20 * 60 + 30) return 'dusk';
  return 'night';
}

export function getTimeOfDayState(date: Date = new Date()): TimeOfDayState {
  const phase = getTimeOfDayPhase(date);
  const minutes = getMinutes(date);
  const states: Record<TimeOfDayPhase, Omit<TimeOfDayState, 'phase' | 'minutes'>> = {
    dawn: {
      label: 'Dawn',
      description: 'A quiet start to the day',
      accent: 'from-amber-300/30 via-orange-200/10 to-transparent',
      icon: 'sunrise',
    },
    day: {
      label: 'Daylight',
      description: 'Clear skies for exploring',
      accent: 'from-sky-300/20 via-cyan-100/10 to-transparent',
      icon: 'sun',
    },
    'golden-hour': {
      label: 'Golden hour',
      description: 'Warm light is settling in',
      accent: 'from-orange-300/35 via-rose-200/15 to-transparent',
      icon: 'sunset',
    },
    dusk: {
      label: 'Dusk',
      description: 'The city is shifting into evening',
      accent: 'from-indigo-400/25 via-violet-300/15 to-transparent',
      icon: 'sunset',
    },
    night: {
      label: 'Night',
      description: 'A softer view for after dark',
      accent: 'from-indigo-950/45 via-slate-900/20 to-transparent',
      icon: 'moon',
    },
  };

  return { phase, minutes, ...states[phase] };
}

export function formatClockTime(minutes: number): string {
  const normalized = normalizeMinutes(minutes);
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${String(mins).padStart(2, '0')} ${suffix}`;
}

export function getApproximateSunTimes(date: Date = new Date()): { sunrise: string; sunset: string } {
  const dayOfYear = Math.floor(
    (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(date.getFullYear(), 0, 0)) /
      86400000,
  );
  const seasonalShift = Math.round(45 * Math.sin(((dayOfYear - 80) / 365) * Math.PI * 2));
  return {
    sunrise: formatClockTime(390 - seasonalShift),
    sunset: formatClockTime(1110 + seasonalShift),
  };
}
